import React, { useCallback, useEffect } from 'react';
import { Button } from '@mui/material';
import { useBannerDismissal } from '../hooks/useBannerDismissal';
import { DismissibleBannerProps } from '../types';
import { DISMISSAL_DURATIONS } from '../constants/dismissalDurations';
import { useThemeContext } from 'contexts/theme';
import { isTenantExpUS } from '@tenants/utils/which-tenant';

interface BannerWithDismissalProps extends DismissibleBannerProps {
  children: (props: {
    isDismissed: boolean;
    onDismiss: () => void;
    actionButton?: React.ReactNode;
  }) => React.ReactNode;
  onButtonClick?: () => void;
}

const BannerWithDismissal: React.FC<BannerWithDismissalProps> = ({
  tenantId,
  persistent = false,
  storageKey,
  storageStrategy = 'cookie',
  dismissalDuration = DISMISSAL_DURATIONS.ONE_DAY,
  buttonText,
  autoHide,
  onDismiss,
  onButtonClick,
  children,
}) => {
  const { theme } = useThemeContext();
  const isExpUSTenant = tenantId ? isTenantExpUS(tenantId) : false;

  const isDismissible = !persistent;

  const { isDismissed, isLoading, dismiss } = useBannerDismissal({
    storageKey: storageKey || '',
    storageStrategy,
    dismissalDurationMs: dismissalDuration,
    persistDismissal: isDismissible && !!storageKey,
  });

  const handleDismiss = useCallback(() => {
    dismiss();
    onDismiss?.();
  }, [dismiss, onDismiss]);

  const handleButtonClick = () => {
    dismiss();
    if (onButtonClick) {
      onButtonClick();
    } else if (onDismiss) {
      onDismiss();
    }
  };

  useEffect(() => {
    if (autoHide && !isDismissed && isDismissible && !isLoading) {
      const timer = setTimeout(handleDismiss, autoHide);
      return () => clearTimeout(timer);
    }
  }, [autoHide, isDismissed, isDismissible, isLoading, handleDismiss]);

  if (isLoading) {
    return null;
  }

  const actionButton = buttonText && isDismissible ? (
    <Button
      variant="contained"
      size="medium"
      onClick={handleButtonClick}
      sx={{
        textTransform: 'none',
        backgroundColor: `${isExpUSTenant ? theme.colors.primary : '#000000'} !important`,
        color: `${isExpUSTenant ? theme.colors.primaryInvertedBackground : '#FFFFFF'} !important`,
        border: 'none !important', // Override Hello Elementor's "1px solid #c36"
        borderRadius: isExpUSTenant ? `${theme.buttons.borderRadiusLarge} !important` : '2px !important',
        paddingX: 2,
        paddingY: 1,
        fontFamily: isExpUSTenant ? `${theme.fonts.text} !important` : 'Mullish, Arial, sans-serif !important',
        fontWeight: isExpUSTenant ? `${theme.fonts.headingBoldWeight} !important` : 'medium !important',
        '&:hover': {
          backgroundColor: `${isExpUSTenant ? theme.colors.secondaryHoverColor : '#333333'} !important`,
          border: 'none !important', // Ensure no border on hover
        },
      }}
    >
      {buttonText}
    </Button>
  ) : undefined;

  return (
    <>
      {children({
        isDismissed,
        onDismiss: handleDismiss,
        actionButton,
      })}
    </>
  );
};

export default BannerWithDismissal;